import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Formik, Form, FormikProps } from "formik"
import SidePanel from "../../side-panel"
import BBModal from "../../modal"
import {
  useToast,
  useValidateEmailAndNMLS,
  usePendingSubscriptionsCount,
  useUserRoles
} from "../../../hooks"
import {
  CreatePendingSubscriptionPayload,
  PendingSubscriptionFull,
  UpdatePendingSubscriptionPayload,
  GooglePlace,
  SubscriptionDetails
} from "../../../../types"
import {
  convertAddressObjToPlaceString,
  convertGooglePlaceToAddressObj,
  validateZipcodeForPayload,
  convertSubscriptionDetailsToPayload
} from "../../../utils"
import {
  useCreatePendingSubscriptionMutation,
  useUpdatePendingSubscriptionMutation,
  useDeletePendingSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useSearchLoanOfficerQuery
} from "../../../../redux/queries"
import ManageEngagementBasic from "../../../../common/ui/engagements/manage/sections/basic"
import ManageEngagementSelectors from "../../../../common/ui/engagements/manage/sections/selectors"
import ManageEngagementMisc from "../../../../common/ui/engagements/manage/sections/misc"
import { useAppSelector } from "../../../hooks"
import { selectUserDetails } from "../../../../redux/slices/auth"
import { dreamRateFormSchema } from "./dream-rate-form-schema"
import { getPendingSubscriptionStatus } from "../../../utils/dream-rate"

type Props = {
  open: boolean
  data?: PendingSubscriptionFull
  onClose: () => void
}

const convertPendingSubscriptionToInitialValues = (
  data?: PendingSubscriptionFull,
  loid?: string
): CreatePendingSubscriptionPayload => {
  const location = data?.quote_info?.location
  const locationObj = {
    city: location?.city ?? "",
    county: location?.county ?? "",
    state: location?.state ?? "",
    street_address: location?.street_address ?? "",
    zipcode: location?.zipcode ?? ""
  }

  return {
    // Basic fields
    active: "1" as const,
    deleted: "0" as const,
    email: data?.email ?? "",
    first_name: data?.first_name ?? "",
    last_name: data?.last_name ?? "",
    phone: data?.phone ?? "",
    loid: data?.loid ?? loid ?? "",

    // Loan scenario fields
    loan_type: data?.quote_info?.loan_type ?? "conventional",
    loan_term: data?.quote_info?.loan_term?.toString() ?? "30",
    arm_term: data?.quote_info?.arm_term?.toString() ?? "",
    loan_purpose: data?.quote_info?.loan_purpose ?? "purchase",
    loan_amount: data?.quote_info?.loan_amount?.toString() ?? "",
    credit_score: data?.quote_info?.credit_score ?? 700,
    property_type: data?.quote_info?.property_type ?? "single_family_home",
    residency_type: data?.quote_info?.residency_type ?? "primary_home",
    list_price: data?.quote_info?.list_price?.toString() ?? "",
    cash_out: data?.quote_info?.cash_out?.toString() ?? "",
    first_time_homebuyer: false,
    va_funding_fee_type:
      data?.quote_info?.loan_type === "va"
        ? data?.quote_info?.va_funding_fee_type ?? ""
        : "",
    nonqm_program:
      data?.quote_info?.loan_type === "nonqm"
        ? data?.quote_info?.nonqm_program ?? ""
        : "",

    // Location fields
    ...locationObj,
    location: convertAddressObjToPlaceString(locationObj),

    // Admin fields
    taxes: data?.quote_info?.admin_fields?.taxes ?? 0,
    insurance: data?.quote_info?.admin_fields?.insurance ?? 0,
    seller_credit: data?.quote_info?.admin_fields?.seller_credit ?? 0,

    // Extra flags
    waive_escrows: data?.quote_info?.waive_escrows ?? false,

    // Rate drop specific
    trigger_rate: data?.trigger_rate ?? 0,
    client_request_id: data?.client_request_id ?? undefined
  } as CreatePendingSubscriptionPayload
}

const ManageDreamRatePanel: FC<Props> = ({ open, data, onClose }) => {
  const { pushErrorToast, pushSuccessToast } = useToast()
  const formRef = useRef<FormikProps<CreatePendingSubscriptionPayload>>(null)
  const isCreateMode = !data?.id
  const { maxReached } = usePendingSubscriptionsCount()

  const [createPending, createState] = useCreatePendingSubscriptionMutation()
  const [updatePending, updateState] = useUpdatePendingSubscriptionMutation()
  const [deletePending, deleteState] = useDeletePendingSubscriptionMutation()
  const [updateSubscription, updateSubState] = useUpdateSubscriptionMutation()
  const {
    validateSubscriptionEmail,
    validateSubscriptionPhone,
    resetSubscriptionCache
  } = useValidateEmailAndNMLS()

  const { isLoanOfficer, isBankAdmin, isSuperAdmin } = useUserRoles()
  const canResolveConflict = isLoanOfficer || isBankAdmin || isSuperAdmin

  const derivedStatus = data ? getPendingSubscriptionStatus(data) : null
  const canDelete =
    !isCreateMode &&
    (derivedStatus === "pending" || derivedStatus === "cancelled")

  const userDetails = useAppSelector(selectUserDetails)
  const selectedBrandId = userDetails?.brand_id

  const [googlePlace, setGooglePlace] = useState<GooglePlace>()
  const [selectedLoanOfficer, setSelectedLoanOfficer] = useState<any>(null)
  const [conflictModalSub, setConflictModalSub] =
    useState<SubscriptionDetails | null>(null)
  const [activeConfirmAction, setActiveConfirmAction] = useState<
    "delete" | "prefill" | null
  >(null)

  const conflictingSubRef = useRef<{
    email?: SubscriptionDetails
    phone?: SubscriptionDetails
  }>({})
  const pendingImportSubRef = useRef<SubscriptionDetails | null>(null)

  // Reset mutation states when panel closes to prevent stale toast messages
  useEffect(() => {
    if (!open) {
      createState.reset()
      updateState.reset()
      deleteState.reset()
      updateSubState.reset()
      setConflictModalSub(null)
      conflictingSubRef.current = {}
    }
  }, [open, createState, updateState, deleteState, updateSubState])

  // Fetch loan officer details when selected
  const { data: loanOfficerData } = useSearchLoanOfficerQuery(
    { loid: selectedLoanOfficer?.loid ?? "" },
    { skip: !selectedLoanOfficer?.loid }
  )

  const initialValues = useMemo(
    () => convertPendingSubscriptionToInitialValues(data, userDetails?.id),
    [data, userDetails?.id]
  )

  const isLoading =
    createState.isLoading || updateState.isLoading || deleteState.isLoading

  const setFormField = useCallback((name: string, value: any) => {
    if (formRef.current) {
      formRef.current.setFieldValue(name, value)
    }
  }, [])

  const handleLoanOfficerChange = useCallback(
    (loid: string) => {
      setFormField("loid", loid)
      setSelectedLoanOfficer({ loid })
    },
    [setFormField]
  )

  // Update form fields when Google Place is selected
  useEffect(() => {
    if (googlePlace) {
      const address = convertGooglePlaceToAddressObj(googlePlace)
      setFormField("city", address.city ?? "")
      setFormField("county", address.county ?? "")
      setFormField("state", address.state ?? "")
      setFormField("street_address", address.street_address ?? "")
      setFormField("zipcode", address.zipcode ?? "")
    }
  }, [googlePlace, setFormField])

  const onSubmit = useCallback(async () => {
    const values = formRef.current?.values
    if (!values) return

    // Minimal front-end validation
    if (!values.email || !values.trigger_rate) {
      pushErrorToast("Email and Trigger rate are required")
      return
    }

    // Zipcode validation - only required when location details are shown
    const zipcodeErrors = validateZipcodeForPayload(values, googlePlace)
    if (zipcodeErrors.zipcode) {
      pushErrorToast(zipcodeErrors.zipcode)
      return
    }

    if (isCreateMode) {
      // Extract NMLS from selected loan officer
      const selectedLO = loanOfficerData?.[0]
      const nmls = selectedLO?.nmls || ""

      // Send the full form values with NMLS - API wrapper will transform to backend format
      const payloadWithNMLS = {
        ...values,
        nmls
      }
      await createPending(payloadWithNMLS)
    } else if (data?.id) {
      const payload: UpdatePendingSubscriptionPayload = {
        id: data.id,
        ...values
      }
      await updatePending(payload)
    }
  }, [
    createPending,
    data?.id,
    isCreateMode,
    pushErrorToast,
    updatePending,
    loanOfficerData,
    googlePlace
  ])

  const onDelete = useCallback(async () => {
    if (!data?.id) return
    await deletePending({ id: data.id })
  }, [data?.id, deletePending])

  useEffect(() => {
    if (deleteState.isSuccess) {
      onClose()
      pushSuccessToast("Deleted dream rate")
      return
    }
    if (deleteState.isError) {
      const msg =
        (deleteState.error as any)?.data?.message ||
        (deleteState.error as any)?.message
      pushErrorToast(msg || "Failed to delete dream rate")
      return
    }
  }, [
    deleteState.isSuccess,
    deleteState.isError,
    deleteState.error,
    onClose,
    pushErrorToast,
    pushSuccessToast
  ])

  useEffect(() => {
    const mutationData = isCreateMode ? createState.data : updateState.data
    const isMutationSuccess = isCreateMode
      ? createState.isSuccess
      : updateState.isSuccess
    const isMutationError = isCreateMode
      ? createState.isError
      : updateState.isError
    const mutationError = isCreateMode ? createState.error : updateState.error
    const responseStatus = (mutationData as any)?.status
    const isExplicitFailureStatus =
      responseStatus === "error" || responseStatus === "failed"
    const apiErrorMessage =
      (mutationData as any)?.message ||
      (mutationError as any)?.data?.message ||
      (mutationError as any)?.message

    // Some endpoints return non-"success" truthy statuses (or omit status)
    // while still succeeding. Treat explicit "error" as failure, otherwise
    // trust RTK Query mutation success state.
    const success = isMutationSuccess && !isExplicitFailureStatus
    const error =
      isMutationError || (isMutationSuccess && isExplicitFailureStatus)

    if (success) {
      onClose()
      pushSuccessToast(
        `Successfully ${isCreateMode ? "created" : "updated"} dream rate`
      )
    } else if (error) {
      pushErrorToast(
        apiErrorMessage ||
          `Failed to ${isCreateMode ? "create" : "update"} dream rate`
      )
    }
  }, [
    createState.error,
    createState.isError,
    createState.isSuccess,
    createState.data,
    isCreateMode,
    onClose,
    pushErrorToast,
    pushSuccessToast,
    updateState.error,
    updateState.isError,
    updateState.isSuccess,
    updateState.data
  ])

  // Handle conflict subscription update result
  useEffect(() => {
    if (updateSubState.isSuccess) {
      const subToImport = pendingImportSubRef.current
      // Reset cache first so the validation triggered by setValues hits the API fresh
      resetSubscriptionCache()
      if (subToImport) {
        const qi = subToImport.quote_info
        const loc = qi?.location
        const locationObj = {
          city: loc?.city ?? "",
          county: loc?.county ?? "",
          state: loc?.state ?? "",
          street_address: loc?.street_address ?? loc?.street ?? "",
          zipcode: loc?.zipcode ?? ""
        }
        // setValues triggers revalidation automatically via validateOnChange
        formRef.current?.setValues({
          // Keep loid, trigger_rate, client_request_id, active, deleted as-is
          loid: formRef.current.values.loid,
          trigger_rate: formRef.current.values.trigger_rate,
          client_request_id: formRef.current.values.client_request_id,
          active: formRef.current.values.active,
          deleted: formRef.current.values.deleted,
          // Personal fields (loid skipped intentionally)
          email: subToImport.email ?? "",
          first_name: subToImport.first_name ?? "",
          last_name: subToImport.last_name ?? "",
          phone: subToImport.phone ?? "",
          // Loan scenario
          loan_type: qi?.loan_type ?? "conventional",
          loan_term: qi?.loan_term?.toString() ?? "30",
          arm_term: qi?.arm_term?.toString() ?? "",
          loan_purpose: qi?.loan_purpose ?? "purchase",
          loan_amount: qi?.loan_amount?.toString() ?? "",
          credit_score: qi?.credit_score ?? 700,
          property_type: qi?.property_type ?? "single_family_home",
          residency_type: qi?.residency_type ?? "primary_home",
          list_price: qi?.list_price?.toString() ?? "",
          cash_out: qi?.cash_out?.toString() ?? "",
          first_time_homebuyer: qi?.first_time_homebuyer ?? false,
          va_funding_fee_type:
            qi?.loan_type === "va" ? qi?.va_funding_fee_type ?? "" : "",
          nonqm_program:
            qi?.loan_type === "nonqm" ? qi?.nonqm_program ?? "" : "",
          // Location
          ...locationObj,
          // Admin fields
          taxes: qi?.admin_fields?.taxes ?? 0,
          insurance: qi?.admin_fields?.insurance ?? 0,
          seller_credit: qi?.admin_fields?.seller_credit ?? 0,
          // Extra flags
          waive_escrows: subToImport.waive_escrows ?? false
        })
        pendingImportSubRef.current = null
      } else {
        // No prefill: rerun validation manually after cache reset
        formRef.current?.validateForm()
      }
      pushSuccessToast("Subscription set to Delete")
      setConflictModalSub(null)
      setActiveConfirmAction(null)
      updateSubState.reset()
    } else if (updateSubState.isError) {
      const msg =
        (updateSubState.error as any)?.data?.message ||
        (updateSubState.error as any)?.message
      pushErrorToast(msg || "Failed to update subscription")
      setActiveConfirmAction(null)
      updateSubState.reset()
    }
  }, [
    updateSubState.isSuccess,
    updateSubState.isError,
    updateSubState.error,
    updateSubState.reset,
    pushSuccessToast,
    pushErrorToast,
    resetSubscriptionCache
  ])

  const onConflict = useCallback(
    (
      field: "email" | "phone",
      conflicting: SubscriptionDetails | undefined
    ) => {
      conflictingSubRef.current[field] = conflicting
    },
    []
  )

  const makeRenderError = useCallback(
    (field: "email" | "phone") => (msg: string) => {
      const conflict = conflictingSubRef.current[field]
      if (!conflict) return msg

      if (!canResolveConflict) return msg

      return (
        <>
          Lead already in Subscriptions. Set its status to{" "}
          <button
            type="button"
            className="underline font-medium"
            onClick={() => setConflictModalSub(conflict)}
          >
            Delete
          </button>
          .
        </>
      )
    },
    [canResolveConflict]
  )

  const handleConfirmDeleteConflict = useCallback(
    async (prefill: boolean) => {
      if (!conflictModalSub) return
      pendingImportSubRef.current = prefill ? conflictModalSub : null
      setActiveConfirmAction(prefill ? "prefill" : "delete")
      await updateSubscription({
        ...convertSubscriptionDetailsToPayload(conflictModalSub),
        id: conflictModalSub.id,
        active: "0",
        deleted: "1"
      })
    },
    [conflictModalSub, updateSubscription]
  )

  const validate = useCallback(
    async (values: CreatePendingSubscriptionPayload) => {
      const errors: any = {}

      Object.assign(
        errors,
        await validateSubscriptionEmail(
          { email: values.email, nmls: "" } as any,
          selectedBrandId || "",
          undefined,
          "dreamRate",
          onConflict
        )
      )

      if (values.phone) {
        Object.assign(
          errors,
          await validateSubscriptionPhone(
            { phone: values.phone } as any,
            selectedBrandId || "",
            undefined,
            "dreamRate",
            onConflict
          )
        )
      }

      // Zipcode validation - only required when location details are shown
      const zipcodeErrors = validateZipcodeForPayload(values, googlePlace)
      Object.assign(errors, zipcodeErrors)

      return errors
    },
    [
      validateSubscriptionEmail,
      validateSubscriptionPhone,
      googlePlace,
      selectedBrandId,
      onConflict
    ]
  )

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={dreamRateFormSchema}
      onSubmit={onSubmit}
      validateOnBlur
      validateOnChange
      validate={validate}
      enableReinitialize
      innerRef={formRef}
    >
      {({ isValid, values, errors, dirty }) => {
        const quoteId = (conflictModalSub?.quote_info as any)?.quote_id

        return (
          <SidePanel
            title={`${isCreateMode ? "Add new" : "Edit"} dream rate`}
            open={open}
            onClose={onClose}
            actions={[
              {
                label: "Delete dream rate",
                type: "danger",
                position: "left",
                onClick: onDelete,
                disabled: isLoading,
                hidden: !canDelete
              },
              {
                label: isCreateMode ? "Add dream rate" : "Update dream rate",
                type: "primary",
                onClick: onSubmit,
                disabled:
                  isLoading ||
                  !isValid ||
                  !dirty ||
                  (isCreateMode && maxReached)
              }
            ]}
          >
            <Form>
              <ManageEngagementBasic
                values={values}
                loSearchParams={{ brand_id: selectedBrandId }}
                onlyAdmins={false}
                setFormField={setFormField}
                setGooglePlace={setGooglePlace}
                onLoanOfficerChange={handleLoanOfficerChange}
                hideStatusField
                googlePlace={googlePlace}
                emailRenderError={makeRenderError("email")}
                phoneRenderError={makeRenderError("phone")}
              />

              <ManageEngagementSelectors
                values={values}
                setFormField={setFormField}
                hasDreamRate
                nonqmEnabled
              />
              <ManageEngagementMisc
                values={values}
                setFormField={setFormField}
                hasWaiveEscrows
              />
            </Form>

            {/* Rendered inside SidePanel so headlessui treats this as a nested Dialog */}
            <BBModal
              show={!!conflictModalSub}
              title="Set subscription to Delete?"
              onClose={() => setConflictModalSub(null)}
              size="sm"
            >
              <p className="text-sm text-gray-600 mb-6">
                This will set the conflicting subscription to Delete. You can
                then continue creating the Dream Rate.
              </p>

              <dl className="space-y-2 text-sm mb-8">
                <div className="flex gap-2">
                  <dt className="font-medium text-gray-700 w-28 shrink-0">
                    Email
                  </dt>
                  <dd className="text-gray-900">{conflictModalSub?.email}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="font-medium text-gray-700 w-28 shrink-0">
                    Name
                  </dt>
                  <dd className="text-gray-900">
                    {[conflictModalSub?.first_name, conflictModalSub?.last_name]
                      .filter(Boolean)
                      .join(" ") || "—"}
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="font-medium text-gray-700 w-28 shrink-0">
                    Phone
                  </dt>
                  <dd className="text-gray-900">
                    {conflictModalSub?.phone || "—"}
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="font-medium text-gray-700 w-28 shrink-0">
                    Subscription ID
                  </dt>
                  <dd className="text-gray-900">{conflictModalSub?.id}</dd>
                </div>
                {quoteId && (
                  <div className="flex gap-2">
                    <dt className="font-medium text-gray-700 w-28 shrink-0">
                      Quote ID
                    </dt>
                    <dd className="text-gray-900">{quoteId}</dd>
                  </div>
                )}
              </dl>

              <div className="flex justify-end gap-3 flex-wrap">
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  onClick={() => setConflictModalSub(null)}
                  disabled={updateSubState.isLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-700 disabled:opacity-50"
                  disabled={updateSubState.isLoading}
                  onClick={() => handleConfirmDeleteConflict(false)}
                >
                  {activeConfirmAction === "delete"
                    ? "Updating…"
                    : "Set to Delete"}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-700 disabled:opacity-50"
                  disabled={updateSubState.isLoading}
                  onClick={() => handleConfirmDeleteConflict(true)}
                >
                  {activeConfirmAction === "prefill"
                    ? "Updating…"
                    : "Set to Delete & prefill form"}
                </button>
              </div>
            </BBModal>
          </SidePanel>
        )
      }}
    </Formik>
  )
}

export default ManageDreamRatePanel
